/**
 * CHECKBOX - Composant atomique
 * Checkbox réutilisable dans toute l'application
 */

import { Check } from 'lucide-react';
import { forwardRef } from 'react';

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  ({ checked, onChange, disabled = false, className = '', label }, ref) => {
    const handleChange = () => {
      if (!disabled) {
        // Le parent gère la logique de toggle, on lui passe juste le nouvel état
        onChange(!checked);
      }
    };

    return (
      <label
        ref={ref}
        className={`inline-flex items-center gap-2 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
      >
        {/* Input natif caché pour l'accessibilité */}
        <input
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          aria-label={label}
        />

        {/* Checkbox visuelle */}
        <div
          className={`relative size-4 flex items-center justify-center rounded-sm shadow-sm transition-all border border-zinc-900 ${
            checked ? 'bg-zinc-900' : 'bg-white hover:bg-zinc-50'
          } ${disabled ? 'opacity-50' : ''}`}
        >
          {checked && <Check className="size-4 text-white pointer-events-none" strokeWidth={2.5} />}
        </div>

        {/* Label optionnel */}
        {label && (
          <span className={`text-sm ${disabled ? 'text-zinc-400' : 'text-zinc-900'}`}>{label}</span>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
